/**
 * embedAuth.js
 *
 * Lightweight module-level state for the public Test Graph embed flow
 * (e.g. dm.cerebralmachines.ai iframing /embed/test-graph/:graphId).
 *
 * The embed page seeds this state once from the URL query (?embed_token,
 * ?embed_account). Every axios client in the app then reads it via
 * ``getEmbedAuth()`` to inject ``X-CM-Embed-Token`` + ``X-CM-Account-Id``
 * headers and to skip the global 401 -> /login redirect.
 *
 * Keeping this out of React state means non-React code (axios interceptors,
 * fetch helpers) can read it synchronously without context plumbing.
 *
 * The token is *not* a long-lived user secret -- the backend enforces a
 * per-token allow-list of accounts + graphs (see backend session
 * middleware ``_resolve_embed_session``). It is safe to ship in the URL of
 * an iframe served from a partner subdomain.
 */

let _embed = null;

/** Returns ``{token, accountId}`` if embed mode is active, otherwise null. */
export function getEmbedAuth() {
  return _embed;
}

export function isEmbedMode() {
  return _embed != null && !!_embed.token;
}

/**
 * Activate embed mode. The token + accountId are kept in module memory
 * only -- never persisted to localStorage so they cannot leak past the
 * iframe lifetime.
 */
export function setEmbedAuth({ token, accountId }) {
  if (!token) return;
  _embed = { token, accountId: accountId || null };
  // Also stash on window for non-axios callers (e.g. raw fetch in
  // graphBuilderApi.runPipelineStream) without an import dependency.
  try {
    window.__CM_EMBED_AUTH__ = { ..._embed };
  } catch (e) { /* SSR / restricted environment */ }
}

export function clearEmbedAuth() {
  _embed = null;
  try { delete window.__CM_EMBED_AUTH__; } catch (e) { /* noop */ }
}

/**
 * Hydrate the embed state from a URL search string. The expected
 * parameters are ``embed_token`` (required) and ``embed_account``
 * (optional, defaults to whatever the backend has configured).
 *
 * Returns true if embed mode was activated.
 */
export function hydrateEmbedAuthFromSearch(search) {
  const params = new URLSearchParams(search || '');
  const token = (params.get('embed_token') || '').trim();
  if (!token) return false;
  const accountId = (params.get('embed_account') || '').trim() || null;
  setEmbedAuth({ token, accountId });
  return true;
}
